import { Fragment, useRef } from "react"
import { cn } from "@/lib/utils"

interface MarqueeProps extends React.ComponentProps<"div"> {
	/** Duración de una vuelta completa (CSS time). Mayor = más lento. */
	duration?: string
	/** Invierte el sentido del desplazamiento. */
	reverse?: boolean
	/** Pausa la animación al pasar el cursor por encima de la fila. */
	pauseOnHover?: boolean
	/**
	 * Si se define, al pasar el cursor la fila **no se detiene**: sigue avanzando a
	 * esta fracción de su velocidad (p. ej. `0.5` = mitad de velocidad). Ajusta el
	 * `playbackRate` de la animación vía Web Animations API, así el cambio es suave
	 * (sin el salto que provocaría cambiar `animation-duration`). Tiene prioridad
	 * sobre `pauseOnHover`.
	 */
	hoverSpeed?: number
	/**
	 * Veces que se repite el contenido dentro de cada grupo. Sube este valor si en
	 * pantallas anchas aparecen huecos: cada grupo debe ser ≥ el ancho del viewport
	 * para que el bucle no muestre espacios vacíos.
	 */
	repeat?: number
}

/**
 * Fila con desplazamiento horizontal infinito y sin saltos. Renderiza dos grupos
 * idénticos (el segundo es `aria-hidden`); al salir el primero, el segundo ocupa
 * su lugar exacto, incluido el gap. Para que nunca se vea un hueco, el contenido
 * se repite `repeat` veces de modo que cada grupo supere el ancho de la pantalla.
 *
 * Reutilizable para cualquier carrusel automático (testimonios, logos, etc.).
 */
export function Marquee({
	className,
	duration = "40s",
	reverse = false,
	pauseOnHover = true,
	hoverSpeed,
	repeat = 2,
	children,
	...props
}: MarqueeProps) {
	const rootRef = useRef<HTMLDivElement>(null)

	// Cuando `hoverSpeed` está definido, controlamos la velocidad por JS en vez de
	// pausar por CSS: `playbackRate` cambia de forma continua (sin salto de posición).
	const setPlaybackRate = (rate: number) => {
		rootRef.current?.getAnimations({ subtree: true }).forEach(a => {
			a.playbackRate = rate
		})
	}

	const usesHoverSpeed = hoverSpeed !== undefined

	return (
		<div
			ref={rootRef}
			className={cn("group/marquee flex gap-[var(--marquee-gap,1rem)] overflow-hidden", className)}
			style={{ "--marquee-duration": duration } as React.CSSProperties}
			onPointerEnter={usesHoverSpeed ? () => setPlaybackRate(hoverSpeed) : undefined}
			onPointerLeave={usesHoverSpeed ? () => setPlaybackRate(1) : undefined}
			{...props}
		>
			{[0, 1].map(copy => (
				<div
					key={copy}
					aria-hidden={copy === 1 || undefined}
					className={cn(
						"flex shrink-0 gap-(--marquee-gap,1rem)",
						"motion-safe:animate-[marquee_var(--marquee-duration)_linear_infinite] motion-reduce:animate-none",
						reverse && "shimmer-reverse",
						pauseOnHover && !usesHoverSpeed && "group-hover/marquee:paused"
					)}
				>
					{Array.from({ length: repeat }, (_, r) => (
						<Fragment key={r}>{children}</Fragment>
					))}
				</div>
			))}
		</div>
	)
}
